import { apiClient, publicApiClient } from './client';

// Types for documents API
export interface ArticleDto {
  id: number;
  userId: number;
  profile_id?: string;
  uuid: string;
  slug?: string;
  meta: {
    title?: string;
    filename?: string;
    contentType?: string;
    description?: string;
    summary?: string;
    [key: string]: any;
  };
  state: string;
  createdAt: string;
  updatedAt: string;
  contentUpdatedAt?: string; // Content update timestamp from Article Editor DO
}

/**
 * Articles API client
 */
export const articlesApi = {
  /**
   * Get all articles for the current user
   * @param state Optional state to filter by (e.g., 'draft', 'published', etc.)
   * @returns Array of articles
   */
  getAll: async (state?: string): Promise<ArticleDto[]> => {
    const params = state ? { state } : {};
    const { data } = await apiClient.get<ArticleDto[]>('/api/articles', { params });
    return data || [];
  },

  /**
   * Get a specific article by UUID
   * @param articleId The UUID of the article
   * @returns The article
   */
  getArticle: async (articleId: string): Promise<ArticleDto> => {
    const { data } = await apiClient.get<ArticleDto>(`/api/articles/${encodeURIComponent(articleId)}`);
    return data;
  },

  /**
   * Create a new article
   * @param file The file to upload
   * @param metadata Optional metadata for the article
   * @param state Optional initial state for the article (defaults to 'draft')
   * @returns The created article
   */
  createArticle: async (articleData: { [key: string]: any }): Promise<ArticleDto> => {
    const { state, ...meta } = articleData;

    const payload = {
      meta: meta,
      state: state || 'draft',
    };

    const { data } = await apiClient.post<ArticleDto>(
      '/api/articles',
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return data;
  },

  /**
   * Update an existing article
   * @param articleId The UUID of the article to update
   * @param updates The updates to apply (metadata, file, and/or state)
   * @returns The updated article
   */
  updateArticle: async (
    articleId: string,
    updates: { meta?: Record<string, any>, state?: string }
  ): Promise<ArticleDto> => {
    const { data } = await apiClient.put<ArticleDto>(
      `/api/articles/${encodeURIComponent(articleId)}`,
      updates,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return data;
  },

  /**
   * Validate if a slug is unique
   * @param articleId The UUID of the article
   * @param slug The slug to validate
   * @returns Validation result
   */
  validateSlug: async (articleId: string, slug: string): Promise<{ valid: boolean; error?: string }> => {
    const { data } = await apiClient.post<{ valid: boolean; error?: string }>(
      `/api/articles/${encodeURIComponent(articleId)}/validate-slug`,
      { slug }
    );
    return data;
  },

  /**
   * Publish an article
   * @param articleId The UUID of the article to publish
   * @param slug Optional custom slug
   * @returns The updated article
   */
  publishArticle: async (articleId: string, slug?: string): Promise<ArticleDto> => {
    const { data } = await apiClient.post<ArticleDto>(
      `/api/articles/${encodeURIComponent(articleId)}/publish`,
      { published: true, slug }
    );
    return data;
  },

  /**
   * Unpublish an article
   * @param articleId The UUID of the article to unpublish
   * @returns The updated article
   */
  unpublishArticle: async (articleId: string): Promise<ArticleDto> => {
    const { data } = await apiClient.post<ArticleDto>(
      `/api/articles/${encodeURIComponent(articleId)}/unpublish`,
      {}
    );
    return data;
  },

  /**
   * Clone an article
   * @param articleId The UUID of the article to clone
   * @returns The new cloned article
   */
  cloneArticle: async (articleId: string): Promise<ArticleDto> => {
    const { data } = await apiClient.post<ArticleDto>(`/api/articles/${encodeURIComponent(articleId)}/clone`);
    return data;
  },

  /**
   * Get the content of an article
   * @param articleId The UUID of the article
   * @returns The article content as a Blob
   */
  updateArticleContent: async (articleId: string, content: string): Promise<void> => {
    await apiClient.post(
      `/api/articles/${encodeURIComponent(articleId)}/content`,
      content,
      {
        headers: {
          'Content-Type': 'text/markdown',
        },
      }
    );
  },

  /**
   * Get the content of an article
   * @param articleId The UUID of the article
   * @returns The article content as a Blob
   */
  getArticleContent: async (articleId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(
      `/api/articles/${encodeURIComponent(articleId)}/content`,
      { responseType: 'blob' }
    );
    return data;
  },

  /**
   * Get the content URL of an article
   * @param articleId The UUID of the article
   * @returns URL to the article content
   */
  getArticleContentUrl: (articleId: string): string => {
    return `${apiClient.defaults.baseURL}/api/articles/${encodeURIComponent(articleId)}/content`;
  },

  /**
   * Get a public article by its UUID (the part after 'art_')
   * This uses a separate, unauthenticated client.
   */
  getPublicArticle: async (uuid: string): Promise<any> => {
    const { data } = await publicApiClient.get(`/public/articles/${uuid}`);
    return data;
  },

  /**
   * Delete an article (soft delete)
   * @param articleId The UUID of the article to delete
   */
  deleteArticle: async (articleId: string): Promise<void> => {
    await apiClient.delete(`/api/articles/${encodeURIComponent(articleId)}`);
  },

  /**
   * Get all unique tags for a given type
   * @param type The type of tags to get (e.g., 'documents', 'assets')
   * @returns Array of tags
   */
  getAllTags: async (type: 'documents' | 'assets' = 'documents'): Promise<string[]> => {
    const { data } = await apiClient.get<string[]>('/api/tags', { params: { type } });
    return data || [];
  },

  /**
   * Get all tags for an article
   * @param articleId The UUID of the article
   * @returns Array of tags
   */
  getArticleTags: async (articleId: string): Promise<string[]> => {
    const { data } = await apiClient.get<string[]>(`/api/articles/${encodeURIComponent(articleId)}/tags`);
    return data || [];
  },

  /**
   * Set all tags for an article, replacing existing ones
   * @param articleId The UUID of the article
   * @param tags The array of tags to set
   */
  setArticleTags: async (articleId: string, tags: string[]): Promise<void> => {
    await apiClient.put(`/api/articles/${encodeURIComponent(articleId)}/tags`, { tags });
  },

  /**
   * Get publication statuses for a single article
   * @param articleId The UUID of the article
   * @returns Array of publications for the article
   */
  getArticlePublications: async (articleId: string): Promise<any[]> => {
    const { data } = await apiClient.get(`/api/articles/${encodeURIComponent(articleId)}/publications`);
    return data || [];
  },

  /**
   * Record or update a publication status for an article and connection.
   */
  recordPublication: async (
    articleId: string,
    payload: {
      connectionId: string;
      platformName?: string;
      status: 'pending' | 'in_progress' | 'published' | 'failed';
      externalUrl?: string;
      externalPostId?: string;
      errorMessage?: string;
    }
  ): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post<{ success: boolean }>(
      `/api/articles/${encodeURIComponent(articleId)}/publications`,
      payload
    );
    return data;
  },

  /**
   * Save article content directly to the durable object
   * This is optimized for frequent saves during editing (e.g., on blur)
   * @param articleId The UUID of the article
   * @param content The markdown content to save
   */
  saveContent: async (articleId: string, content: string): Promise<void> => {
    await apiClient.post(
      `/api/articles/${encodeURIComponent(articleId)}/content/save`,
      content,
      {
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    );
  },

  /**
   * Get published article content directly from R2
   * @param articleId The UUID of the article
   * @returns The article content as a string
   */
  getLiveContent: async (articleId: string): Promise<string> => {
    const { data } = await apiClient.get<string>(
      `/api/articles/${encodeURIComponent(articleId)}/content/live`,
      {
        headers: {
          'Accept': 'text/plain',
        }
      }
    );
    return data;
  },

  /**
   * Check if the durable object has content for this article
   * @param articleId The UUID of the article
   * @returns Boolean indicating if content exists
   */
  hasContent: async (articleId: string): Promise<boolean> => {
    try {
      const response = await apiClient.head(
        `/api/articles/${encodeURIComponent(articleId)}/content/live`
      );
      return response.status === 200;
    } catch (error) {
      return false;
    }
  },

  /**
   * Initialize a transient draft durable object
   * @param transientId The temporary ID for the transient draft
   * @param content Initial content for the transient draft
   */
  initializeTransient: async (transientId: string, content: string = ''): Promise<void> => {
    await apiClient.post(
      `/api/articles/${encodeURIComponent(transientId)}/initialize-transient`,
      content,
      {
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    );
  },

  /**
   * Cleanup a transient draft after successful save
   * @param transientId The ID of the transient draft to cleanup
   */
  cleanupTransientDraft: async (transientId: string): Promise<void> => {
    await apiClient.delete(`/api/articles/${encodeURIComponent(transientId)}/cleanup-transient`);
  },

  // ===== Featured Summary APIs =====

  /**
   * Get available featured summary templates and tones
   * @returns Object containing templates and tones
   */
  getFeaturedSummaryTemplates: async (): Promise<{
    templates: { id: string; name: string; description: string; icon: string }[];
    tones: { id: string; name: string }[];
  }> => {
    const { data } = await apiClient.get('/api/gen/featured-summary/templates');
    return data;
  },

  /**
   * Generate a featured summary for an article
   * @param articleId The article ID
   * @param templateId The template to use
   * @param toneId Optional tone ID
   * @returns Generated summary and metadata
   */
  generateFeaturedSummary: async (
    articleId: string,
    templateId: string,
    toneId?: string
  ): Promise<{ summary: string; templateId: string; characterCount: number }> => {
    const { data } = await apiClient.post('/api/gen/featured-summary/generate', {
      articleId,
      templateId,
      toneId,
    });
    return data;
  },

  /**
   * Save a featured summary for an article
   * @param articleId The article ID
   * @param featuredSummary The summary to save
   */
  saveFeaturedSummary: async (
    articleId: string,
    featuredSummary: string,
    featuredSummaryLayout: 'vertical' | 'horizontal' = 'vertical',
    featuredCtaText: string = 'Read full story'
  ): Promise<{ success: boolean; articleId: string; featuredSummary: string; featuredSummaryLayout: string; featuredCtaText: string }> => {
    const { data } = await apiClient.put(`/api/gen/featured-summary/${encodeURIComponent(articleId)}`, {
      featuredSummary,
      featuredSummaryLayout,
      featuredCtaText,
    });
    return data;
  },
};
